import { useState, useRef } from "react";
import { Briefcase, Paperclip, X } from "lucide-react";
import Honeypot from "../../../../components/honeypot";
import { useFormTiming } from "../../../../components/useFormTiming";
import { useTranslation } from "../../../../i18n/useTranslation";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const Careers = () => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="band-hero section text-center">
        <div className="eyebrow">{t("careers.eyebrow")}</div>
        <h1>{t("careers.heading")}</h1>
        <div className="max-w-xl mx-auto pt-3">
          <p className="mini">{t("careers.intro")}</p>
        </div>
      </div>

      <div className="band-a section-tight">
        <div className="max-w-3xl mx-auto flex flex-col gap-6 px-5">
          <RoleCard
            entity={t("careers.roleBharatEntity")}
            title={t("careers.roleBharatTitle")}
            location={t("careers.roleBharatLocation")}
            body={t("careers.roleBharatBody")}
          />
          <RoleCard
            entity={t("careers.roleUsaEntity")}
            title={t("careers.roleUsaTitle")}
            location={t("careers.roleUsaLocation")}
            body={t("careers.roleUsaBody")}
          />
          <RoleCard
            entity={t("careers.roleGlobalEntity")}
            title={t("careers.roleGlobalTitle")}
            location={t("careers.roleGlobalLocation")}
            body={t("careers.roleGlobalBody")}
          />
        </div>
      </div>

      <div className="band-b section-tight">
        <div className="max-w-xl mx-auto text-center">
          <div className="eyebrow">{t("careers.applyHeading")}</div>
          <p className="mini pt-2">{t("careers.applySub")}</p>
        </div>
        <div className="max-w-md mx-auto px-5 pt-6">
          <ApplicationForm />
        </div>
      </div>
    </div>
  );
};

const RoleCard = ({ entity, title, location, body }) => (
  <div className="card-ledger text-left flex flex-col md:flex-row gap-5 md:items-start">
    <Briefcase size={32} strokeWidth={1.5} style={{ color: "var(--gold)" }} className="shrink-0" />
    <div>
      <span className="tag-verdigris">{entity}</span>
      <h3 className="pt-2">{title}</h3>
      <p className="ledger-code">{location}</p>
      <p className="mini pt-2">{body}</p>
    </div>
  </div>
);

const ApplicationForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", role: "", link: "", message: "" });
  const [files, setFiles] = useState([]);
  const [hp, setHp] = useState("");
  const startedAt = useFormTiming();
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked].slice(0, 5)); // cap at 5, matches backend limit
    if (fileInputRef.current) fileInputRef.current.value = ""; // allows re-picking the same file if removed
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("role", form.role);
    fd.append("link", form.link);
    fd.append("message", form.message);
    fd.append("hp", hp);
    fd.append("startedAt", String(startedAt));
    files.forEach((f) => fd.append("attachments", f));

    try {
      const res = await fetch(`${API_BASE}/api/v1/careers-apply`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setStatus("error");
        setError(data?.error || `Request failed (${res.status})`);
        return;
      }
      setStatus("sent");
    } catch {
      setStatus("error");
      setError("Network error — please try again.");
    }
  };

  if (status === "sent") {
    return <p className="mini text-center">{t("careers.sentMessage")}</p>;
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
      <input className="field" placeholder={t("careers.namePlaceholder")} required value={form.name} onChange={update("name")} />
      <input type="email" className="field" placeholder={t("careers.emailPlaceholder")} required value={form.email} onChange={update("email")} />
      <select className="field" required value={form.role} onChange={update("role")}>
        <option value="" disabled>{t("careers.rolePlaceholder")}</option>
        <option value="ceo-bharat">{t("careers.roleBharatTitle")} — {t("careers.roleBharatEntity")}</option>
        <option value="ceo-usa">{t("careers.roleUsaTitle")} — {t("careers.roleUsaEntity")}</option>
        <option value="cpo-global">{t("careers.roleGlobalTitle")} — {t("careers.roleGlobalEntity")}</option>
      </select>
      <input className="field" placeholder={t("careers.linkPlaceholder")} value={form.link} onChange={update("link")} />
      <textarea className="field" placeholder={t("careers.messagePlaceholder")} rows="4" required value={form.message} onChange={update("message")} />

      <div>
        <label className="mini flex items-center gap-2 cursor-pointer">
          <Paperclip size={16} />
          <span>{t("careers.attachmentsLabel")}</span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={addFiles}
          />
        </label>
        <p className="mini text-xs opacity-70 pt-1">{t("careers.attachmentsHint")}</p>

        {files.length > 0 && (
          <ul className="flex flex-col gap-1 pt-2">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between text-xs mini">
                <span className="truncate">{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} aria-label="Remove file">
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button type="submit" className="btn-brass self-center mt-1" disabled={status === "sending"}>
        {status === "sending" ? t("careers.sendingBtn") : t("careers.sendBtn")}
      </button>
      {status === "error" && <p className="mini text-center" style={{ color: "#c0392b" }}>{error}</p>}
    </form>
  );
};

export default Careers;
